import classNames from 'classnames';
import { CSSProperties, FC, ReactElement, RefObject, useMemo, useRef } from 'react';
import { ICustomTooltipProps, TOOLTIP_POSITION } from '../../CustomTooltip';
import { LOCALES } from '~/constants/main';
import { localeStore } from '~/mobx/localesStore';

type TProps = Pick<
  ICustomTooltipProps,
  'message' | 'messageOption' | 'position' | 'gapTop' | 'minWidth' | 'maxWidth' | 'tooltipClassName' | 'messageClassName'
>;
interface IProps extends TProps {
  visible: boolean;
  wrapperRef: RefObject<HTMLDivElement>;
  isCopyType: boolean;
}

const CustomTooltipMessage: FC<IProps> = ({ visible, isCopyType, ...props }: IProps): ReactElement => {
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const isAbsolutePosition = useMemo((): boolean => props.position === TOOLTIP_POSITION.ABSOLUTE, [props.position]);

  const position = (): CSSProperties => {
    if (!props.wrapperRef.current?.getClientRects()[0] || !tooltipRef.current?.getClientRects()[0]) return {};

    const isArLanguage: boolean = localeStore.language === LOCALES.AR;
    const { width: wrapperWidth, y: wrapperY, x: wrapperX } = props.wrapperRef.current?.getClientRects()[0];
    const { width: tooltipWidth, height: tooltipHeight } = tooltipRef.current.getClientRects()[0];
    let top: number = 0;
    let left: number = 0;

    if (!isAbsolutePosition) {
      if (!isCopyType) {
        top = wrapperY - props.gapTop - tooltipHeight;
        left = wrapperX - tooltipWidth / 2 + wrapperWidth / 2;
      } else if (isCopyType) {
        top = wrapperY - (props.gapTop + 5) - tooltipHeight;
        left = isArLanguage ? wrapperX : wrapperX + wrapperWidth - tooltipWidth;
      }

      if (window.innerWidth < left + tooltipWidth) left = window.innerWidth - tooltipWidth - 20;
      if (left < 20) left = 20;
    } else {
      if (!isCopyType) {
        top = -(tooltipHeight + props.gapTop);
        left = -(tooltipWidth / 2 - wrapperWidth / 2);
      } else if (isCopyType) {
        top = -(tooltipHeight + props.gapTop);
        left = isArLanguage ? 0 : -(tooltipWidth - wrapperWidth);
      }
    }

    return { top, left };
  };

  const className = useMemo((): string => {
    const typeClassName: string = isCopyType ? 'tooltip-copied' : 'tooltip-information';
    return classNames(
      'tooltip',
      typeClassName,
      { 'tooltip_show tooltip__animation': visible, tooltip__fixed: !isAbsolutePosition },
      props.tooltipClassName
    );
  }, [isCopyType, visible, isAbsolutePosition, props.tooltipClassName]);

  return (
    <div ref={tooltipRef} className={className} style={{ ...position(), maxWidth: props.maxWidth, minWidth: props.minWidth }}>
      <p className={classNames('tooltip__message', props.messageClassName)}>{localeStore.t(`${props.message}`, props.messageOption)}</p>
    </div>
  );
};

export default CustomTooltipMessage;
